import Price from "@/components/Price"
import { placeHolderImage } from "@/constants/placeHolderImage"
import { useIsPreorder } from "@/hooks/MiniCart/useIsPreorder"
import { useIsMobile } from "@/hooks/use-is-mobile"
import { formatString } from "@/lib/format-string"
// import {
//   getNewUrlKey
// } from "@/lib/get-product-full-detail-seo"
import { useMiniCartStore } from "@/components/MiniCart/use-new-minicart-store"
import { Button } from "@/components/ui/button"
import {
  Credenza,
  CredenzaBody,
  CredenzaContent,
  CredenzaTrigger,
} from "@/components/ui/credenza"
import { cn } from "@/lib/utils"
import { ConfigurableProduct } from "@/types/graphql.prod"
import resourceUrl from "@/util/makeUrl"
import Image from "next/image"
import Link from "next/link"
import { useMemo, useState } from "react"
import LoadingIndicator from "../LoadingIndicator"
import { useProductData } from "../Product/use-product-data"
import { getSortedColor } from "../ProductFullDetail/get-sorted-color"
import { ProductFullDetailQuickView } from "../ProductFullDetail/product-full-detail-quick-view"
import { CustomizeOptions } from "./CustomizeOptions/customizeOptions"
import { MaybeRating } from "./maybe-rating"
import { useGetImage } from "./use-get-image"
import { useGTM } from "./use-gtm"

export const GalleryItem = ({
  item,
  showReview = true,
  showCustomizeOptions = true,
}: {
  item: ConfigurableProduct
  showReview?: boolean
  showCustomizeOptions?: boolean
}) => {
  const { hoverImage, productImage, selectedColor, setSelectedColor } =
    useGetImage({
      item,
    })
  const allColorCount = item?.show_colors?.total

  const colorOptions = useMemo(() => {
    if (item.show_colors?.items?.length) {
      return item.show_colors.items
    }

    return []
  }, [item])

  const { handleLinkClick } = useGTM({
    item,
  })

  const { name, price_range, url_key, rating_summary, review_count } =
    item || {}

  const productLink = resourceUrl(`/${url_key}.html`)
  const priceSource =
    price_range.minimum_price.final_price ||
    price_range.minimum_price.regular_price
  const { minimum_price: minPrice } = price_range
  const specialPrice =
    minPrice?.final_price?.value &&
    minPrice?.regular_price?.value &&
    minPrice?.final_price?.value < minPrice?.regular_price?.value
      ? minPrice.regular_price.value
      : undefined
  const { configurable_options } = item || {}
  const { isHasPreOrder } = useIsPreorder({
    product: item,
    configurable_options,
  })
  const outOfStock = useMemo(() => {
    if (isHasPreOrder) {
      return false
    }

    if (item.stock_status === "OUT_OF_STOCK") {
      return true
    }
  }, [isHasPreOrder, item.stock_status])

  const isPreOrder = item?.presale_product?.status

  const setCartStatus = useMiniCartStore((state) => state?.setCartStatus)

  if (!item) {
    return null
  }

  return (
    <div data-cy="GalleryItem-root" className="relative flex flex-col">
      <div className="group relative mb-[10px] lg_mb-[20px]">
        <Link
          onClick={handleLinkClick}
          href={productLink}
          className={"relative w-full overflow-hidden"}
        >
          {productImage && (
            <Image
              width={640}
              height={640}
              src={productImage}
              quality={100}
              className="duration-300 w-full transition-opacity group-hover_opacity-0"
              alt={name ?? ""}
              placeholder={placeHolderImage}
            />
          )}
          {hoverImage && (
            <div className="duration-300 absolute inset-0 opacity-0 transition-opacity group-hover_opacity-100">
              <Image
                width={640}
                height={640}
                src={hoverImage}
                quality={100}
                className="w-full"
                alt={name ?? ""}
                placeholder={placeHolderImage}
              />
            </div>
          )}
        </Link>
        <div className="absolute inset-x-[15px] bottom-[15px] hidden lg_block">
          <HoverQuickView
            newUrlKey={item?.url_key ?? ""}
            afterSubmit={() => {
              setCartStatus("addtocart")
            }}
          />
        </div>
      </div>

      {showCustomizeOptions && colorOptions?.length > 0 ? (
        <CustomizeOptions
          allColorCount={allColorCount ?? 0}
          colorOptions={colorOptions}
          selectedColor={selectedColor}
          setSelectedColor={setSelectedColor}
          outOfStock={outOfStock}
          url_key={url_key ?? ""}
        />
      ) : null}
      <Link
        style={{
          display: "-webkit-box",
          WebkitBoxOrient: "vertical",
          WebkitLineClamp: 2,
        }}
        onClick={handleLinkClick}
        href={productLink}
        className="mb-[5px] overflow-hidden font-sans text-sm leading-[1.4] text-title hover_text-primary lg_text-[16px]"
      >
        {formatString(name ?? "")}
      </Link>

      <div className="mb-[10px] flex items-center justify-start">
        <div className="font-poppins text-[16px] font-[600] leading-[1.4] text-title lg_text-[18px]">
          <Price
            value={priceSource.value}
            currencyCode={priceSource.currency}
          />
        </div>

        {outOfStock ? (
          <div className="ml-[10px] w-[100px] rounded-[20px] bg-[#B52F1D] text-center font-sans text-sm leading-[20px] text-white">
            Out Of Stock
          </div>
        ) : null}
      </div>

      <MaybeRating
        rating_summary={rating_summary}
        review_count={review_count}
        showReview={showReview}
      />

      {!isPreOrder && (
        <>
          <MaybeNew productMark={item?.product_mark || ""} />
          <MaybeSale productMark={item?.product_mark || ""} />
          <MaybeBestSeller productMark={item?.product_mark || ""} />
        </>
      )}
      <MaybePreOrder isPreOrder={isPreOrder} />
      <div className="mt-[10px] lg_hidden">
        <HoverQuickView
          newUrlKey={item?.url_key ?? ""}
          afterSubmit={() => {
            setCartStatus("addtocart")
          }}
        />
      </div>
    </div>
  )
}

function MaybePreOrder({
  isPreOrder,
}: {
  isPreOrder: boolean | null | undefined
}) {
  if (!isPreOrder) {
    return null
  }

  return (
    <div className="absolute left-[6px] top-[10px] rounded-[20px] border-1 border-primary bg-white px-[12px] font-sans text-sm leading-[24px] text-primary">
      Pre-Order
    </div>
  )
}

function MaybeNew({ productMark }: { productMark: string }) {
  // if (!productMark.toLowerCase().includes("new")) {
  //   return null
  // }

  return (
    <div className="absolute left-[6px] top-[10px] rounded-[20px] border-1 border-primary bg-white px-[12px] font-sans text-sm leading-[24px] text-primary">
      New
    </div>
  )
}

function MaybeBestSeller({ productMark }: { productMark: string }) {
  if (!productMark.toLowerCase().includes("best seller")) {
    return null
  }

  return (
    <div className="absolute left-[6px] top-[10px] rounded-[20px] border-1 border-white bg-primary px-[12px] font-sans text-sm leading-[24px] text-white">
      Best Seller
    </div>
  )
}

function MaybeSale({ productMark }: { productMark: string }) {
  if (!productMark.toLowerCase().includes("sale")) {
    return null
  }

  return (
    <div className="absolute left-[6px] top-[10px] rounded-[20px] border-1 border-primary bg-white px-[12px] font-sans text-sm leading-[24px] text-primary">
      Sale
    </div>
  )
}

export function HoverQuickView({
  newUrlKey,
  className,
  afterSubmit,
}: {
  newUrlKey: string
  className?: string
  afterSubmit?: any
}) {
  const [open, setOpen] = useState(false)
  const [seoColorLabel, setSeoColorLabel] = useState("")
  const [quickViewSuccessOpen, setQuickViewSuccessOpen] = useState(false)
  const [quickViewSuccessNumber, setQuickViewSuccessNumber] = useState(1)

  const { loading, product } = useProductData({
    newUrlKey: newUrlKey,
    skip: !open,
  })

  const defaultValues = useMemo(() => {
    return product?.configurable_options?.reduce(
      (acc: Record<string, any>, opts: any) => {
        if (opts?.attribute_code?.toLowerCase().startsWith("color")) {
          const sortedColor = getSortedColor({
            values: opts?.values,
            variants: product.variants,
            attribute_code: opts?.attribute_code,
          })
          const firstColor = sortedColor[0]

          acc[opts?.attribute_code] = {
            uid: firstColor?.uid,
            label: firstColor?.label,
            option_attribute_id: opts?.attribute_id,
            option_selection_value: firstColor?.value_index,
          }
        }

        if (opts?.label?.toLowerCase().startsWith("size")) {
          acc[opts?.attribute_code] = undefined
        }

        return acc
      },
      {
        quantity: "1",
      }
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product])

  const handleColorLabelChange = (colorLabel: string) => {
    if (colorLabel === seoColorLabel) {
      return
    }

    setSeoColorLabel(colorLabel)
    // handleRefreshUrlKey(colorLabel)
  }

  return (
    <>
      <Credenza open={open} onOpenChange={setOpen} direction="bottom">
        <CredenzaTrigger asChild>
          <Button
            variant={"outline"}
            className={cn(
              "flex",
              "items-center justify-center",
              "group-hover_opacity-100 lg_opacity-0",
              "relative group-hover_flex",
              "w-full rounded-[10px] border-primary bg-primary font-poppins text-sm font-[600] text-white transition-all",
              className
            )}
          >
            <span className="uppercase">Choose Options</span>
          </Button>
        </CredenzaTrigger>
        <CredenzaContent
          className="max-h-[calc(100vh-95px)] max-w-[1200px] rounded-tl-[20px] rounded-tr-[20px] pt-12 lg_rounded-[20px] lg_px-[40px] lg_pt-[107px]"
          closeClassName="h-8 w-8 "
          showCloseButton={false}
        >
          <div
            className="absolute right-[24px] top-[24px] z-10 flex h-[20px] w-[20px] cursor-pointer items-center justify-center lg_right-[40px] lg_top-[40px] lg_h-[28px] lg_w-[28px] lg_bg-transparent"
            onClick={() => {
              setOpen(false)
            }}
          >
            <svg
              width="28"
              height="28"
              viewBox="0 0 28 28"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M21.5 21.5L6.5 6.5M21.5 6.5L6.5 21.5"
                stroke="#666666"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
          <CredenzaBody className="lg_pb-4m h-fit space-y-4 overflow-auto px-0 pb-20 pr-[10px] text-sm sm_pb-0 sm_text-left lg_max-h-[700px] lg_overflow-hidden  lg_pr-0">
            <ProductWrapper
              loading={loading}
              product={product}
              defaultValues={defaultValues}
              handleColorLabelChange={handleColorLabelChange}
              seoColorLabel={seoColorLabel}
              setOpen={setOpen}
              setQuickViewSuccessOpen={setQuickViewSuccessOpen}
              setQuickViewSuccessNumber={setQuickViewSuccessNumber}
              afterSubmit={afterSubmit}
            />
          </CredenzaBody>
        </CredenzaContent>
      </Credenza>
      {/* <MobileAddToCartSuccessTipQuickView
        addToCartStatus={quickViewSuccessOpen}
        addToCartQty={quickViewSuccessNumber}
        setQuickViewSuccessOpen={setQuickViewSuccessOpen}
      /> */}
    </>
  )
}

function ProductWrapper({
  loading,
  product,
  defaultValues,
  handleColorLabelChange,
  seoColorLabel,
  setOpen,
  setQuickViewSuccessOpen,
  setQuickViewSuccessNumber,
  afterSubmit,
}: any) {
  if (loading) {
    return (
      <div className="flex min-h-[calc(100dvh-95px)] items-center justify-center lg_min-h-[680px]">
        <LoadingIndicator />
      </div>
    )
  }

  if (!product) {
    return <div className="min-h-[calc(100dvh-95px)] lg_min-h-[680px] " />
  }

  return (
    <ProductFullDetailQuickView
      product={product}
      defaultValues={defaultValues}
      handleColorLabelChange={handleColorLabelChange}
      seoColorLabel={seoColorLabel}
      setOpen={setOpen}
      setQuickViewSuccessOpen={setQuickViewSuccessOpen}
      setQuickViewSuccessNumber={setQuickViewSuccessNumber}
      afterSubmit={afterSubmit}
    />
  )
}
